import { ApiError } from "@altek/ui/lib/problem";
import type { FileScanState } from "@altek/ui/models/files/file-scan";
import { appErrorMessage } from "./error-messages";
import { realtime } from "@/lib/realtime/realtime-client";
import type { FileStatusPayload } from "@/lib/realtime/realtime-events";

/**
 * YÜKLEMEDEN SONRAKİ BEKLEME — «Taranıyor» (KARARLAR K3).
 *
 * `POST /files/{id}/complete` artık dosyayı AÇMIYOR: satır `SCANNING` oluyor ve işçi ClamAV +
 * içerik denetiminden geçirip karar veriyor. Dosya CLEAN olmadan hiçbir yere bağlanamıyor,
 * gönderilemiyor, indirilemiyor — yani yükleme akışlarının hepsi bu kararı beklemek zorunda.
 *
 * TEK YERDE, çünkü sekiz yükleme yolu var (sohbet, not, Dosyalarım, şirket rafı, dönüştürücü,
 * profil fotoğrafı, şirket logosu, kanal raporu) ve sekiz ayrı bekleme döngüsü yazmak, sekiz ayrı
 * zaman aşımı ve sekiz farklı cümle demekti.
 *
 * İKİ KANAL, BİRİ YETERLİ. Canlı `file.status` çerçevesi geldiğinde bekleme ANINDA bitiyor;
 * çerçeve hiç gelmezse (SSE kopuk, sekme arka planda, çerçeve kayıp) yoklama aynı cevabı biraz
 * daha geç veriyor. Bir kanalın çalışmaması, yüklemenin sonsuza kadar «Taranıyor» kalması demek
 * olmamalı — bu yüzden ikisi de var ve ikisi de tek başına yeterli.
 *
 * REDDİ HATAYA ÇEVİRİYOR: zararlı, etkin içerikli, şifreli ya da taranamamış bir dosya için
 * sunucunun kendi kodunu (`FILE_INFECTED`, `FILE_ACTIVE_CONTENT`, `FILE_ENCRYPTED`,
 * `FILE_SCAN_FAILED`) taşıyan bir `ApiError` fırlatıyor, böylece ekranlar cümleyi ortak hata
 * kataloğundan alıyor (`error-messages.ts`) ve kendi metinlerini yazmıyor.
 */

export interface FileScanStatus {
  fileId: string;
  status: FileScanState;
  /** Reddedildiyse hata kataloğunun kodu; yoksa null. */
  resultCode: string | null;
  /** Hangi kural konuştu: `MACRO`, `PDF_JAVASCRIPT`, `SIGNATURE`, `UNREACHABLE`… */
  reason: string | null;
  scannedAt: string | null;
  /**
   * Karantinadaki baytları indirme eylemi BU kullanıcıya açık mı (K4). Kararı sunucu veriyor:
   * ekran yetkiyi kendi hesaplamıyor, ve kapalıysa düğmeyi hiç çizmiyor.
   */
  canDownloadQuarantined?: boolean;
}

/** Durumu okuyan uç: genel `/files/{id}/status` (`file-scan.ts`) ya da bir modülün kendi kapısı. */
export type FileScanReader = (fileId: string, signal?: AbortSignal) => Promise<FileScanStatus>;

/** Tarama bitti mi — `PENDING` ve `SCANNING` yolda demek. */
export function isSettled(status: FileScanState): boolean {
  return status === "CLEAN" || status === "INFECTED" || status === "SCAN_FAILED";
}

export interface WaitForScanOptions {
  signal?: AbortSignal | undefined;
  /** Her durum değişiminde çağrılır: satırın «Taranıyor» yazmasını sağlayan şey bu. */
  onState?: ((status: FileScanState) => void) | undefined;
  /** Bu süre sonunda `FILE_NOT_READY` ile vazgeçilir (dosya silinmez; tarama sürüyor olabilir). */
  timeoutMs?: number | undefined;
  /** Test dikişi. */
  now?: (() => number) | undefined;
  sleep?: ((ms: number, signal?: AbortSignal) => Promise<void>) | undefined;
};

/** İlk yoklama 600 ms sonra, sonra 1.5 kat, en fazla 3 sn: küçük dosya birkaç saniyede biter. */
const FIRST_DELAY_MS = 600;
const MAX_DELAY_MS = 3_000;
const DEFAULT_TIMEOUT_MS = 180_000;

/**
 * Dosya kullanılabilir olana kadar bekler. CLEAN dönerse çağıran devam eder; ret kodlu bir
 * `ApiError` olur; süre dolarsa `FILE_NOT_READY`. Durumu `read` okur: `files.file.read` taşımayan dış hesap
 * (şoför, 2 Kasım 2026) kendi kapısından sorar; bekleme, zaman aşımı ve ret cümlesi aynı kalır.
 */
export async function waitForScanWith(
  read: FileScanReader,
  fileId: string,
  options: WaitForScanOptions = {},
): Promise<FileScanStatus> {
  const now = options.now ?? (() => Date.now());
  const pause = options.sleep ?? sleep;
  const deadline = now() + (options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  let reported: FileScanState | null = null;

  // Canlı çerçeve: beklemeyi kısaltıyor, ama yokluğu beklemeyi durdurmuyor.
  let nudged = false;
  const stopListening = realtime.on("file.status", (frame) => {
    const payload = frame.data as unknown as FileStatusPayload;
    if (payload?.fileId === fileId) nudged = true;
  });

  try {
    let delay = FIRST_DELAY_MS;
    for (;;) {
      throwIfAborted(options.signal);
      const status = await read(fileId, options.signal);
      if (status.status !== reported) {
        reported = status.status;
        options.onState?.(status.status);
      }
      if (status.status === "CLEAN") return status;
      if (isSettled(status.status)) throw refusal(status);
      if (now() >= deadline) {
        throw new ApiError({
          status: 409,
          code: "FILE_NOT_READY",
          serverMessage: "Dosyanın güvenlik taraması beklenenden uzun sürdü. Biraz sonra tekrar deneyin.",
        });
      }
      // Çerçeve geldiyse hemen yeniden sor; gelmediyse geri çekilerek.
      await pause(nudged ? 0 : delay, options.signal);
      nudged = false;
      delay = Math.min(MAX_DELAY_MS, Math.round(delay * 1.5));
    }
  } finally {
    stopListening();
  }
}

/**
 * Sunucunun kararını istemci hatasına çevirir — cümle ortak katalogdan gelsin diye.
 *
 * `serverMessage` DA GEREKÇEYİ SÖYLÜYOR (belge-duzeltme 20 Ekim 2026, H17). Kit 3.20'den beri
 * `messageFor` sunucunun cümlesini sözlüğün genel cümlesinin önüne koyuyor; burada her redde aynı
 * «Dosya güvenlik taramasından geçemedi.» yazılınca şifreli PDF de makrolu Word de zararlı yazılım
 * gibi okunuyordu. Cümle artık kod + gerekçeden kuruluyor («Parola korumalı…», «Makro…»); hangi
 * yardımcıyla okunursa okunsun aynı söz.
 */
export function refusal(status: FileScanStatus): ApiError {
  const code = status.resultCode ?? (status.status === "INFECTED" ? "FILE_INFECTED" : "FILE_SCAN_FAILED");
  const details = status.reason ? { reason: status.reason } : undefined;
  return new ApiError({
    status: 422,
    code,
    serverMessage: appErrorMessage(code, details) ?? "Dosya güvenlik taramasından geçemedi.",
    ...(details ? { details } : {}),
  });
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException("Bekleme iptal edildi", "AbortError");
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Bekleme iptal edildi", "AbortError"));
      },
      { once: true },
    );
  });
}
